import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
    const [email, setEmail] = useState("");
    const [subscribed, setSubscribed] = useState(false);

    const onSubscribe = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            toast.error("Please enter a valid email.");
            return;
        }
        try {
            await axios.post(`${API}/newsletter`, { email });
            setSubscribed(true);
            setEmail("");
            toast.success("Subscribed — see you in your inbox.");
        } catch (err) {
            toast.error("Could not subscribe. Try again later.");
        }
    };

    return (
        <footer
            data-testid="footer"
            className="relative bg-ink border-t border-white/10 grain"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <div className="reveal flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-ember mb-8">
                            <span className="block w-8 h-px bg-ember" />
                            Stay close
                        </div>
                        <h3 className="reveal font-display text-bone text-3xl lg:text-4xl tracking-tight max-w-[24ch] mb-8 leading-tight">
                            A monthly dispatch on craft, taste and the studios
                            we admire.{" "}
                            <span className="text-bone-500">No spam.</span>
                        </h3>
                        <form
                            onSubmit={onSubscribe}
                            data-testid="newsletter-form"
                            className="flex items-center max-w-[480px] border border-white/15 focus-within:border-ember transition-colors"
                        >
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@studio.com"
                                data-testid="newsletter-input"
                                className="flex-1 bg-transparent outline-none px-4 py-4 text-bone placeholder:text-bone-500 text-sm"
                            />
                            <button
                                type="submit"
                                data-testid="newsletter-submit"
                                className="btn-ember h-12 px-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]"
                            >
                                {subscribed ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    <ArrowUpRight className="w-4 h-4" />
                                )}
                                {subscribed ? "Done" : "Subscribe"}
                            </button>
                        </form>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-4">
                            Studio
                        </div>
                        <ul className="space-y-3 text-sm text-bone-300">
                            <li>
                                <a
                                    href="/#about"
                                    className="hover:text-ember transition-colors"
                                >
                                    About
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/#work"
                                    className="hover:text-ember transition-colors"
                                >
                                    Work
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/#insights"
                                    className="hover:text-ember transition-colors"
                                >
                                    Insights
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/#pricing"
                                    className="hover:text-ember transition-colors"
                                >
                                    Pricing
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/careers"
                                    className="hover:text-ember transition-colors"
                                >
                                    Careers
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/portal/login"
                                    data-testid="footer-client-login"
                                    className="text-ember hover:text-ember-light transition-colors"
                                >
                                    Client login →
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className="lg:col-span-3">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-4">
                            Elsewhere
                        </div>
                        <ul className="space-y-3 text-sm text-bone-300">
                            {["Instagram", "LinkedIn", "Behance", "Are.na"].map(
                                (s) => (
                                    <li key={s}>
                                        <a
                                            href="#"
                                            data-testid={`social-${s.toLowerCase()}`}
                                            className="link-underline hover:text-ember transition-colors"
                                        >
                                            {s}
                                        </a>
                                    </li>
                                ),
                            )}
                        </ul>
                    </div>
                </div>

                {/* Massive brand wordmark */}
                <div className="mt-20 lg:mt-28 reveal">
                    <h2 className="font-display font-medium text-bone tracking-tightest leading-[0.85] text-[28vw] lg:text-[22vw]">
                        Aximo
                        <span className="text-ember font-serif-i italic font-normal">
                            .
                        </span>
                    </h2>
                </div>

                <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-bone-500">
                    <p>
                        © {new Date().getFullYear()} Aximo Studios, LDA. All
                        rights reserved.
                    </p>
                    <div className="flex items-center gap-6">
                        <a href="#" className="hover:text-bone">
                            Privacy
                        </a>
                        <a href="#" className="hover:text-bone">
                            Terms
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
